import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { apiGet } from '@/api/client';
import type { Department, MeDepartment } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { visibleDepartments } from '@/features/me/permissions';
import { CompareChart } from '@/features/statistics/CompareChart';
import { useAsync } from '@/lib/useAsync';

type Row = Department | MeDepartment;

function scopeOf(row: Row): { id: string; company_id: string | null } {
  return { id: row.id ?? '', company_id: row.company_id ?? null };
}

/** `Department.id`/`company_id` are optional in the generated schema
 * (never actually absent on a real row) — `visibleDepartments`/
 * `DepartmentScope` need them guaranteed, so every fetched list is
 * normalized once here. */
function withId(
  rows: readonly Department[],
): (Department & { id: string; company_id: string | null })[] {
  return rows.map((d) => ({ ...d, id: d.id ?? '', company_id: d.company_id ?? null }));
}

/**
 * `/departments` — the *Departments* overview (§ Content-gebied › Drie
 * lagen). Always scoped to the active company (§ Bedrijfscontext): a real
 * company fetches its departments, and the personal context (no company at
 * all) falls back to `me.departments`. `visibleDepartments` narrows every
 * branch to what the caller may act on — a coach never sees "all
 * departments", only their own (§ Zichtbaarheid, column D).
 */
export function DepartmentsPage() {
  const { company } = useActiveCompany();

  if (company === null) {
    return <PersonalDepartments />;
  }
  return <CompanyDepartments companyId={company.id} companyName={company.name} />;
}

function PersonalDepartments() {
  const { t } = useTranslation();
  const { me, permissions } = useMe();
  const rows = visibleDepartments(permissions, null, me.departments ?? []);

  return (
    <DepartmentsLayout
      title={t('departments.page.title')}
      lead={t('departments.page.leadPersonal')}
      rows={rows}
    />
  );
}

function CompanyDepartments({
  companyId,
  companyName,
}: {
  companyId: string;
  companyName: string;
}) {
  const { t } = useTranslation();
  const { permissions } = useMe();
  const departments = useAsync(
    () => apiGet('/companies/{companyId}/departments', { params: { companyId } }),
    [companyId],
  );

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('departments.page.title')}
        lead={t('departments.page.leadCompany', { company: companyName })}
        actions={
          permissions.canManageDepartments(companyId) ? (
            <Button
              as={Link}
              to={`/manage/companies/${companyId}/departments/new`}
              state={{ from: '/departments' }}
            >
              {t('departments.list.newDepartment')}
            </Button>
          ) : undefined
        }
      />

      <Card padding="none">
        <div className="p-5 pb-0">
          <CardHeader title={t('departments.list.cardTitle')} />
        </div>
        <AsyncSection state={departments} label={t('departments.list.cardTitle')} skeleton="table">
          {(all) => {
            const rows = visibleDepartments(permissions, companyId, withId(all));
            return rows.length === 0 ? (
              <div className="p-5 pt-0">
                <EmptyState text={t('departments.list.empty')} />
              </div>
            ) : (
              <DepartmentTable rows={rows} showCompanyColumn={false} />
            );
          }}
        </AsyncSection>
      </Card>

      <AsyncSection state={departments} label={t('departments.compare.cardTitle')} skeleton="card">
        {(all) => {
          const statsTargets = visibleDepartments(permissions, companyId, withId(all)).filter((d) =>
            permissions.canViewDepartmentStats(scopeOf(d)),
          );
          if (statsTargets.length === 0) return null;
          return (
            <CompareChart
              title={t('departments.compare.cardTitle')}
              metricKey="steps"
              targets={statsTargets.map((d) => ({ department_id: d.id ?? '' }))}
              detailTo="/departments/compare"
            />
          );
        }}
      </AsyncSection>
    </section>
  );
}

function DepartmentsLayout({
  title,
  lead,
  rows,
}: {
  title: string;
  lead: string;
  rows: MeDepartment[];
}) {
  const { t } = useTranslation();
  const { permissions } = useMe();
  const statsTargets = rows.filter((d) => permissions.canViewDepartmentStats(scopeOf(d)));

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={title} lead={lead} />

      <Card padding="none">
        <div className="p-5 pb-0">
          <CardHeader title={t('departments.list.cardTitle')} />
        </div>
        {rows.length === 0 ? (
          <div className="p-5 pt-0">
            <EmptyState text={t('departments.list.empty')} />
          </div>
        ) : (
          <DepartmentTable rows={rows} showCompanyColumn={false} />
        )}
      </Card>

      {statsTargets.length > 0 && (
        <CompareChart
          title={t('departments.compare.cardTitle')}
          metricKey="steps"
          targets={statsTargets.map((d) => ({ department_id: d.id ?? '' }))}
          detailTo="/departments/compare"
        />
      )}
    </section>
  );
}

function DepartmentTable({ rows, showCompanyColumn }: { rows: Row[]; showCompanyColumn: boolean }) {
  const { t } = useTranslation();

  return (
    <DataTable
      rowKey={(d) => d.id ?? ''}
      rows={rows}
      columns={[
        {
          key: 'name',
          header: t('departments.list.name'),
          render: (d) => <Link to={`/departments/${d.id}`}>{d.name}</Link>,
        },
        ...(showCompanyColumn
          ? [
              {
                key: 'company',
                header: t('departments.list.company'),
                render: (d: Row) =>
                  d.company_id ? (
                    <Link to={`/manage/companies/${d.company_id}`}>{d.company_id}</Link>
                  ) : (
                    <Badge tone="outline">{t('departments.list.unattached')}</Badge>
                  ),
              },
            ]
          : []),
        {
          key: 'statistics',
          header: t('departments.list.statistics'),
          render: (d) => <Badge tone="outline">{d.stats_visibility}</Badge>,
        },
        {
          key: 'collectiveConsent',
          header: t('departments.list.collectiveConsent'),
          render: (d) =>
            d.collective_consent ? (
              <Badge tone="mint">{t('departments.list.inForce')}</Badge>
            ) : (
              t('departments.list.none')
            ),
        },
      ]}
    />
  );
}
